import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import Card from '../../components/dashboard/Card'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'

export default function AffirmationsPage() {
  const [affirmations, setAffirmations] = useState([])
  const [filteredAffirmations, setFilteredAffirmations] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, scheduled, sent
  const [showModal, setShowModal] = useState(false)
  const [editingAffirmation, setEditingAffirmation] = useState(null)
  const [formData, setFormData] = useState({
    text: '',
    scheduled_datetime: ''
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    console.log('✅ AffirmationsPage v2.0 NEW - Using scheduled_datetime column')
    loadAffirmations()
  }, [])

  useEffect(() => {
    filterAffirmations()
  }, [affirmations, filter])

  const loadAffirmations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        toast.error('Not authenticated')
        return
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      if (userError) throw userError

      const { data, error: fetchError } = await supabase
        .from('affirmations')
        .select('*')
        .eq('org_id', userData.org_id)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setAffirmations(data || [])
    } catch (error) {
      console.error('Load affirmations error:', error)
      toast.error('Failed to load affirmations')
    } finally {
      setLoading(false)
    }
  }

  const filterAffirmations = () => {
    let filtered = [...affirmations]

    switch (filter) {
      case 'scheduled':
        filtered = filtered.filter(a => a.scheduled_datetime && !a.sent_at)
        break
      case 'sent':
        filtered = filtered.filter(a => a.sent_at)
        break
      default:
        // all - no filter
        break
    }

    setFilteredAffirmations(filtered)
  }

  const getStatus = (affirmation) => {
    if (affirmation.sent_at) {
      return { label: 'Sent', color: 'green', icon: '✓' }
    }
    if (affirmation.scheduled_datetime) {
      const scheduled = new Date(affirmation.scheduled_datetime)
      const now = new Date()
      
      if (scheduled < now) {
        return { label: 'Overdue', color: 'red', icon: '!' }
      }
      return { label: 'Scheduled', color: 'yellow', icon: '📅' }
    }
    return { label: 'Pending', color: 'blue', icon: '🔔' }
  }

  const sendAffirmation = async (text, orgId) => {
    try {
      // Call Edge Function to send push notifications
      const { data: { session } } = await supabase.auth.getSession()
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-affirmation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            text,
            org_id: orgId
          })
        }
      )

      if (!response.ok) {
        throw new Error('Failed to send affirmation')
      }

      return true
    } catch (error) {
      console.error('Send affirmation error:', error)
      throw error
    }
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    
    // Validate
    const trimmedText = formData.text.trim()
    if (!trimmedText) {
      toast.error('Message cannot be empty')
      return
    }

    if (trimmedText.length > 500) {
      toast.error('Message too long (max 500 characters)')
      return
    }

    setSaving(true)
    const loadingToast = toast.loading('Creating affirmation...')

    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      // If no schedule datetime, send immediately
      const shouldSendNow = !formData.scheduled_datetime

      const { data: newAffirmation, error: createError } = await supabase
        .from('affirmations')
        .insert({
          org_id: userData.org_id,
          text: trimmedText,
          scheduled_datetime: formData.scheduled_datetime || null,
          sent_at: shouldSendNow ? new Date().toISOString() : null,
          created_by: user.id
        })
        .select()
        .single()

      if (createError) throw createError

      // Send immediately if not scheduled
      if (shouldSendNow) {
        await sendAffirmation(trimmedText, userData.org_id)
        toast.success('Affirmation sent to all members!', { id: loadingToast })
      } else {
        toast.success('Affirmation scheduled successfully!', { id: loadingToast })
      }

      setShowModal(false)
      setFormData({ text: '', scheduled_datetime: '' })
      await loadAffirmations()
    } catch (error) {
      console.error('Create affirmation error:', error)
      toast.error('Failed to create affirmation: ' + error.message, { id: loadingToast })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async (e) => {
    e.preventDefault()
    
    const trimmedText = formData.text.trim()
    if (!trimmedText) {
      toast.error('Message cannot be empty')
      return
    }

    if (trimmedText.length > 500) {
      toast.error('Message too long (max 500 characters)')
      return
    }

    setSaving(true)
    const loadingToast = toast.loading('Updating affirmation...')

    try {
      const { error: updateError } = await supabase
        .from('affirmations')
        .update({
          text: trimmedText,
          scheduled_datetime: formData.scheduled_datetime || null
        })
        .eq('id', editingAffirmation.id)

      if (updateError) throw updateError

      toast.success('Affirmation updated!', { id: loadingToast })
      setShowModal(false)
      setEditingAffirmation(null)
      setFormData({ text: '', scheduled_datetime: '' })
      await loadAffirmations()
    } catch (error) {
      console.error('Edit affirmation error:', error)
      toast.error('Failed to update: ' + error.message, { id: loadingToast })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (affirmation) => {
    if (!window.confirm('Delete this affirmation? This cannot be undone.')) {
      return
    }

    const loadingToast = toast.loading('Deleting...')

    try {
      const { error: deleteError } = await supabase
        .from('affirmations')
        .delete()
        .eq('id', affirmation.id)

      if (deleteError) throw deleteError

      toast.success('Affirmation deleted', { id: loadingToast })
      await loadAffirmations()
    } catch (error) {
      console.error('Delete affirmation error:', error)
      toast.error('Failed to delete: ' + error.message, { id: loadingToast })
    }
  }

  const openCreateModal = () => {
    setEditingAffirmation(null)
    setFormData({ text: '', scheduled_datetime: '' })
    setShowModal(true)
  }

  const openEditModal = (affirmation) => {
    setEditingAffirmation(affirmation)
    setFormData({
      text: affirmation.text,
      scheduled_datetime: affirmation.scheduled_datetime || ''
    })
    setShowModal(true)
  }

  const stats = {
    total: affirmations.length,
    scheduled: affirmations.filter(a => a.scheduled_datetime && !a.sent_at).length,
    sent: affirmations.filter(a => a.sent_at).length
  }

  if (loading) {
    return <LoadingSpinner message="Loading affirmations..." />
  }

  return (
    <DashboardLayout>
      <PageHeader
        title="Words of Affirmation"
        description="Send and schedule encouraging messages for your members"
      >
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create Affirmation
        </button>
      </PageHeader>

      <div className="p-6 space-y-6">
        {/* Filter Tabs */}
        <div className="flex gap-2 border-b">
          {[
            { id: 'all', label: 'All', count: stats.total },
            { id: 'scheduled', label: 'Scheduled', count: stats.scheduled },
            { id: 'sent', label: 'Sent', count: stats.sent }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                filter === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Affirmations List */}
        <Card>
          {filteredAffirmations.length === 0 ? (
            <div className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gray-300 mb-4">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <p className="text-gray-500 text-lg font-medium mb-2">
                {filter === 'all' ? 'No affirmations yet' : `No ${filter} affirmations`}
              </p>
              <p className="text-gray-400 text-sm mb-6">
                Create your first affirmation to encourage your members
              </p>
              {filter === 'all' && (
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Create First Affirmation
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAffirmations.map((affirmation) => {
                const status = getStatus(affirmation)
                const canEdit = affirmation.scheduled_datetime && !affirmation.sent_at
                
                return (
                  <div
                    key={affirmation.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Status Badge */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            status.color === 'green' ? 'bg-green-100 text-green-700' :
                            status.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                            status.color === 'red' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            <span>{status.icon}</span>
                            {status.label}
                          </span>
                          
                          {affirmation.scheduled_datetime && !affirmation.sent_at && (
                            <span className="text-xs text-gray-500">
                              Sends: {new Date(affirmation.scheduled_datetime).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          )}

                          {affirmation.sent_at && (
                            <span className="text-xs text-gray-500">
                              Sent: {new Date(affirmation.sent_at).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          )}
                        </div>

                        {/* Text */}
                        <p className="text-gray-900 text-base leading-relaxed">
                          {affirmation.text}
                        </p>

                        {/* Metadata */}
                        <p className="text-xs text-gray-500 mt-2">
                          Created {new Date(affirmation.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* Edit - Only for scheduled, not sent */}
                        {canEdit && (
                          <button
                            onClick={() => openEditModal(affirmation)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                        )}

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(affirmation)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingAffirmation ? 'Edit Affirmation' : 'Create New Affirmation'}
            </h2>

            <form onSubmit={editingAffirmation ? handleEdit : handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  required
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Write an encouraging message for your members..."
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.text.length}/500 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule Send (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_datetime}
                  onChange={(e) => setFormData({ ...formData, scheduled_datetime: e.target.value })}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank to send immediately. Otherwise, schedule for any future date and time.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    setEditingAffirmation(null)
                    setFormData({ text: '', scheduled_datetime: '' })
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingAffirmation ? 'Update' : 'Create & Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}